<template>
    <div>
        <page-title>{{ $t('menu.canteen_calendar') }}</page-title>
        <div>
            <v-chip
                v-model="showWeekend"
                class="border-g-e8 bg-white"
                filter
                @click="filterWeekends"
                >{{ $t('labels.weekends') }}</v-chip
            >
        </div>
        <v-row>
            <v-col>
                <form-panel v-if="date" id="canteen-calendar">
                    <div class="navs">
                        <v-btn icon @click="prevMonth">
                            <v-icon>navigate_before</v-icon>
                        </v-btn>
                        <v-btn icon @click="nextMonth">
                            <v-icon>navigate_next</v-icon>
                        </v-btn>
                        <template v-if="currentLocale === 'en'">
                            {{ date.format('MMMM YYYY') }}
                        </template>
                        <template v-else>
                            {{ date.format('YYYY MMMM') }}
                        </template>
                    </div>
                    <div class="days" :class="{ 'show-weekends': showWeekend }">
                        <div
                            v-for="(day, index) in days"
                            :key="`${currentLocale}-${index}`"
                            class="day"
                            :class="{
                                'last-month':
                                    day.format('M') < today.format('M'),
                            }"
                        >
                            <div class="day-header">
                                <div
                                    class="date"
                                    :class="{
                                        today: today.isSame(day, 'day'),
                                    }"
                                >
                                    {{ day.format('D') }}
                                </div>
                                {{ day.format('ddd') }}
                            </div>
                            <div>
                                <div
                                    v-for="(order, orderIndex) in getOrders(
                                        day
                                    )"
                                    :key="orderIndex"
                                    class="order"
                                >
                                    <v-img
                                        :aspect-ratio="16 / 9"
                                        style="
                                            max-height: 112px;
                                            margin-bottom: 10px;
                                        "
                                        width="100%"
                                        class="rounded"
                                        :src="
                                            order.relatedRestaurants[0]
                                                .cardImageMedia.public_url
                                        "
                                    />
                                    <div class="desc">
                                        <div
                                            v-for="(restaurant,
                                            restaurantIndex) in order.relatedRestaurants"
                                            :key="restaurantIndex"
                                            class="restaurant"
                                        >
                                            <div
                                                class="font-weight-bold text--primary"
                                            >
                                                {{ restaurant.restaurantName }}
                                            </div>
                                            <div class="text--secondary">
                                                {{ $t('orders.cutoff') }}:
                                                {{
                                                    restaurant.cutoffTimeOverride
                                                        ? restaurant.cutoffTimeOverride
                                                        : order.cutoffTime
                                                          | transformDate
                                                }}
                                            </div>
                                            <div>
                                                {{
                                                    order.budgetLimit | currency
                                                }}
                                                {{ $t('labels.per_person') }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form-panel>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import CloudcanteensApiClient from '@/api/RestApi/CompanyCloudcanteens'
import PageTitle from '@/components/layout/components/pageTitle.vue'
import FormPanel from '@/components/form/FormPanel.vue'
import { mapGetters } from 'vuex'
import configType from '@/store/type/configType'

export default {
    components: { FormPanel, PageTitle },
    data() {
        return {
            today: null,
            date: null,
            items: [],
            showWeekend: false,
        }
    },
    computed: {
        ...mapGetters({
            currentLocale: configType.getters.CURRENT_LOCALE,
        }),
        days() {
            if (!this.date) {
                return []
            }
            const items = []
            let start
            if (this.date.format('M') === this.today.format('M')) {
                start = this.date.startOf('isoWeek')
            } else {
                start = this.date.startOf('month').startOf('isoWeek')
            }
            const end = this.date.endOf('month').endOf('isoWeek')

            while (start < end) {
                items.push(start)

                if (!this.showWeekend && start.format('d') === '5') {
                    start = start.add(3, 'day')
                } else {
                    start = start.add(1, 'day')
                }
            }
            return items
        },
    },
    watch: {
        currentLocale() {
            this.$forceUpdate()
        },
    },
    mounted() {
        this.date = this.$date()
        this.today = this.$date()

        this.loadItems()
    },
    methods: {
        filterWeekends() {
            this.showWeekend = !this.showWeekend
        },
        async loadItems() {
            const start = this.date.utc().startOf('month')
            const end = this.date.utc().endOf('month')

            this.items = (
                await CloudcanteensApiClient.list({
                    'deliveryTime[after]': start.toDate(),
                    'deliveryTime[before]': end.toDate(),
                })
            )['hydra:member']
        },
        getOrders(date) {
            return this.items.filter((x) => {
                return this.$date(x.deliveryTime).isSame(date, 'day')
            })
        },
        nextMonth() {
            this.date = this.date.add(1, 'month')
            this.loadItems()
        },
        prevMonth() {
            this.date = this.date.subtract(1, 'month')
            this.loadItems()
        },
    },
}
</script>
<style scoped lang="scss">
#canteen-calendar {
    .navs {
        font-weight: bold;
        font-size: 26px;
        margin-bottom: 10px;
    }
    .days {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
        grid-gap: 1px;
        outline: 1px solid #eee;
        border-radius: 5px;
        overflow: hidden;

        &.show-weekends {
            grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
        }

        .day {
            padding: 10px;
            min-height: 100px;
            outline: 1px solid #eee;

            &.last-month {
                opacity: 0.6;
            }

            .day-header {
                display: flex;
                align-items: center;
                line-height: 1;
                margin-bottom: 10px;

                .date {
                    border-radius: 50%;
                    font-size: 18px;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;

                    &.today {
                        background: #333;
                        color: white;
                        margin-right: 6px;
                    }
                }
            }

            .order {
                font-size: 12px;

                img {
                    border-radius: 4px;
                    display: block;
                    margin-bottom: 4px;
                }

                .desc {
                    padding: 4px;
                    line-height: 1.2;

                    .text--primary {
                        font-size: 13px;
                    }

                    div {
                        margin-bottom: 4px;
                    }

                    .restaurant {
                        margin-bottom: 8px;
                    }
                }
            }
        }
    }
}
</style>
